import { useState, useEffect, useCallback, Fragment } from 'react';
import { ShieldAlert, Filter, Check, X, ChevronDown, ChevronRight, LayoutGrid, List, Laptop, ChevronsUp } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { parseHash, viewToHash } from '../lib/router.jsx';
import {
  assignBon,
  buildCveTaskText,
  ComitBonAssignModal,
  ComitBonChip,
  ComitTaskButton,
  useComitConfig,
} from '../lib/comit.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { VulnerabilityDeviceTree } from '../components/VulnerabilityDeviceTree.jsx';

// Sévérités classées pour trier les groupes logiciel d'un appareil (vue « Par
// poste », niveau 2) — même échelle que `SeverityRank` côté serveur.
const SEV_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

const SEV_BADGE = {
  CRITICAL: 'bg-crit/15 border-crit/30 text-crit',
  HIGH: 'bg-warn/15 border-warn/30 text-warn',
  MEDIUM: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  LOW: 'bg-slate-500/15 border-slate-500/30 text-muted',
  UNKNOWN: 'bg-slate-500/10 border-slate-500/20 text-muted-2',
};

const PAGE_SIZES = [50, 100, 200];
// `bontravail.description` est en varchar(100) côté ComIT — même limite que
// buildCveTaskText/buildAlertTaskText dans lib/comit.jsx.
const MAX_DESCRIPTION = 100;

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

/**
 * Auteur d'une résolution, lisible. `system:*` vient de CveService (la CVE a
 * disparu du scan) ; sinon c'est le courriel du technicien. Vide = ligne résolue
 * avant l'ajout de la colonne ResolvedBy : on n'affiche alors rien plutôt que
 * d'attribuer à tort.
 */
const fmtResolvedBy = (by) => {
  if (!by) return '';
  if (by === 'system:cve-scan') return 'Système (logiciel à jour)';
  if (by.startsWith('system:')) return 'Système';
  return by;
};

const SevBadge = ({ sev }) => (
  <span
    className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase border ${
      SEV_BADGE[sev] || SEV_BADGE.UNKNOWN
    }`}
  >
    {sev || 'unknown'}
  </span>
);

/**
 * Clé composite d'un groupe (logiciel, version) — sert de clé React et
 * d'identifiant de sélection/expansion. Un `` sépare les deux champs :
 * ni l'un ni l'autre ne peut le contenir, contrairement à un simple espace.
 */
const groupKey = (g) => `${g.softwareName}${g.softwareVersion}`;

/**
 * Descriptions d'un bon issu d'un GROUPE de vulnérabilités (page Vulnérabilités,
 * vue « Par logiciel »). Distinct de `buildCveTaskText` (lib/comit.jsx), qui
 * décrit une seule ligne (appareil, CVE) : ici le travail couvre potentiellement
 * plusieurs appareils d'un coup.
 */
function buildGroupTaskText(group) {
  const logiciel = [group.softwareName, group.softwareVersion].filter(Boolean).join(' ');
  const devices = group.devices || [];
  const nb = devices.length;
  const courte = `RMM — mise à jour ${logiciel || 'logiciel'} (${nb} appareil${nb > 1 ? 's' : ''})`;

  const lignes = [
    'Bon créé depuis un groupe de vulnérabilités Fonxtio RMM.',
    '',
    `Logiciel     : ${logiciel || '—'}`,
    `Appareils (${nb}) : ${devices.map((d) => d.computerName).join(', ') || '—'}`,
    `CVE (${group.cveIds?.length || 0}) : ${(group.cveIds || []).join(', ') || '—'}`,
    `Sévérité max : ${group.maxSeverity || '—'}${
      group.maxCvssScore != null ? ` (CVSS ${group.maxCvssScore.toFixed(1)})` : ''
    }`,
  ];

  return { description: courte.slice(0, MAX_DESCRIPTION), descriptiontravail: lignes.join('\n').trim() };
}

export function VulnerabilitiesPage({ initialSeverity, initialClientId, initialAgentId, initialAgentName }) {
  // Le ClientPicker tape /api/clients (ViewClients) : sans la permission on masque
  // le filtre plutôt que d'afficher un champ mort (même règle qu'AlertsPage).
  const { navigate, can } = useDashboard();
  const canFilterByClient = can('ViewClients');

  // Filtres et vue : lus dans l'URL comme sur AlertsPage — sans ça, F5 ou un
  // lien partagé perdrait la vue et tous les filtres posés.
  const urlQ = parseHash().query;
  const numOrNull = (v) => (v === undefined || v === '' ? null : Number(v));

  // « Par poste » est la vue par défaut : légère au chargement (une ligne par
  // appareil), contrairement aux deux autres qui matérialisent tout de suite
  // des milliers de lignes (cf. CLAUDE.md, page Vulnérabilités).
  const [view, setView] = useState(
    urlQ.view === 'software' ? 'software' : urlQ.view === 'cve' ? 'cve' : 'device'
  );
  const [severity, setSeverity] = useState(initialSeverity || urlQ.severity || '');
  // Trois états ('', 'true', 'false') sérialisés en 'all' dans l'URL — une valeur
  // vide y disparaîtrait sinon (viewToHash élague les chaînes vides), et « toutes »
  // ne survivrait pas à un F5.
  const [resolved, setResolved] = useState(
    urlQ.resolved === 'all' ? '' : urlQ.resolved === 'true' ? 'true' : 'false'
  );
  const [clientId, setClientId] = useState(initialClientId || urlQ.clientId || null);
  // Filtre appareil : posé en arrivant depuis une alerte CVE, qui annonce un
  // nombre de CVE sans dire lesquelles. Retirable — sinon on arriverait sur une
  // liste tronquée sans comprendre pourquoi le reste du parc a disparu.
  const [agentId, setAgentId] = useState(initialAgentId || urlQ.agentId || null);
  // Champ de recherche affiché immédiatement ; `search` (envoyé au serveur) est
  // débounced pour ne pas déclencher une requête à chaque frappe.
  const [searchInput, setSearchInput] = useState(urlQ.search || '');
  const [search, setSearch] = useState(urlQ.search || '');
  const [page, setPage] = useState(Math.max(1, numOrNull(urlQ.page) || 1));
  const [pageSize, setPageSize] = useState(PAGE_SIZES.includes(Number(urlQ.pageSize)) ? Number(urlQ.pageSize) : 50);

  const [vulns, setVulns] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [scanStatuses, setScanStatuses] = useState([]);
  const [scanStatusError, setScanStatusError] = useState(false);
  // Le tri vit ici, pas dans un composant enfant : la table est démontée
  // pendant chaque chargement, un état interne au tableau serait donc perdu à
  // chaque changement de filtre ou de page (cf. CLAUDE.md, gotcha useTableSort).
  const { sortRows, headerProps, arrow } = useTableSort('cvssScore', 'desc');

  // ─── Vue « Par poste » (arborescence appareil ▸ logiciel ▸ CVE) ──────────
  // Niveau 1 : une ligne par appareil, chargée avec les autres filtres.
  const [deviceRows, setDeviceRows] = useState([]);
  const [deviceTotal, setDeviceTotal] = useState(0);
  // Tri client des lignes appareil : hook séparé de celui de la vue « Par
  // CVE » ci-dessus (colonnes différentes). Clé initiale nulle : tant que
  // l'utilisateur ne clique pas un en-tête, l'ordre du serveur (critique ▸
  // élevée ▸ total ▸ nom) est conservé.
  const deviceSort = useTableSort(null, 'desc');
  // Appareils dépliés / logiciels dépliés : état de COMPOSANT, pas d'URL —
  // sans intérêt à partager par lien, et grossirait la query string au fil des
  // clics (cf. tâche : « Expanded device ids kept in component state only »).
  const [expandedDevices, setExpandedDevices] = useState(() => new Set());
  const [expandedSoftware, setExpandedSoftware] = useState(() => new Set()); // clés `${agentId}::${softwareKey}`
  // Niveau 2 par appareil, chargé une seule fois à la première expansion et
  // gardé en cache tant que les filtres ne changent pas (cf. `load`, qui vide
  // ce cache à chaque rechargement de la liste d'appareils).
  const [deviceDetails, setDeviceDetails] = useState({}); // agentId -> { loading, error, softwareGroups }
  const [selectedDeviceSoftware, setSelectedDeviceSoftware] = useState([]); // clés `${agentId}::${softwareKey}`

  // Liaison à un bon de travail ComIT : écriture sur les lignes, donc réservée à
  // ManageVulnerabilities. Sans la permission, ni cases à cocher ni déliaison —
  // le bouton « Tâche ComIT » reste, il ne fait qu'ouvrir l'écran de ComIT.
  const comit = useComitConfig();
  const canAssignBon = comit.enabled && can('ManageVulnerabilities');
  const [selected, setSelected] = useState([]); // vue « Par CVE » : ids de vulnérabilité
  const [selectedGroups, setSelectedGroups] = useState([]); // vue « Par logiciel » : clés de groupe
  const [assignOpen, setAssignOpen] = useState(false);

  // Débounce de la recherche : 300 ms sans frappe avant d'interroger le serveur.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (resolved !== '') params.set('resolved', resolved);
    if (clientId) params.set('clientId', clientId);
    if (agentId) params.set('agentId', agentId);
    if (search) params.set('search', search);
    if (view === 'cve') {
      params.set('page', page);
      params.set('pageSize', pageSize);
      const r = await api.get('/platform/vulnerabilities?' + params.toString());
      if (r) {
        setVulns(Array.isArray(r.items) ? r.items : []);
        setTotal(r.total || 0);
        setTotalPages(r.totalPages || 0);
      }
    } else if (view === 'software') {
      const r = await api.get('/platform/vulnerabilities/by-software?' + params.toString());
      setGroups(Array.isArray(r) ? r : []);
    } else {
      // « Par poste » : une seule requête agrégée, le détail par appareil est
      // chargé à part (loadDeviceDetail), à l'expansion.
      const r = await api.get('/platform/vulnerabilities/by-device?' + params.toString());
      setDeviceRows(Array.isArray(r?.items) ? r.items : []);
      setDeviceTotal(r?.total || 0);
    }
    setLoading(false);
  }, [view, severity, resolved, clientId, agentId, search, page, pageSize]);

  useEffect(() => {
    // Vider la sélection à chaque rechargement : garder des identifiants de
    // lignes/groupes qui ne sont plus affichés assignerait un bon à l'aveugle.
    setSelected([]);
    setSelectedGroups([]);
    // Idem pour l'arborescence « Par poste » : un jeu de filtres différent
    // rend le cache de détail par appareil obsolète (les compteurs niveau 1
    // changent, le détail niveau 2/3 doit être rechargé sous les mêmes filtres).
    setExpandedDevices(new Set());
    setExpandedSoftware(new Set());
    setDeviceDetails({});
    setSelectedDeviceSoftware([]);
    load();
  }, [load]);

  /**
   * Détail niveau 2 (logiciels) + niveau 3 (CVE, déjà inclus dans la réponse)
   * d'un appareil, chargé à la première expansion. Réutilise l'endpoint liste
   * existant filtré par `agentId`, avec les mêmes filtres actifs que la page —
   * paginé à 200 et parcouru si l'appareil a plus de 200 lignes (rare : c'est
   * déjà un sous-ensemble d'un seul poste).
   */
  const loadDeviceDetail = useCallback(
    async (targetAgentId) => {
      setDeviceDetails((d) => ({ ...d, [targetAgentId]: { loading: true, error: false, softwareGroups: [] } }));
      try {
        const params = new URLSearchParams();
        params.set('agentId', targetAgentId);
        if (severity) params.set('severity', severity);
        if (resolved !== '') params.set('resolved', resolved);
        if (search) params.set('search', search);
        params.set('pageSize', '200');

        let all = [];
        let p = 1;
        let totalPages1 = 1;
        do {
          params.set('page', String(p));
          const r = await api.get('/platform/vulnerabilities?' + params.toString());
          if (!r) throw new Error('fetch failed');
          all = all.concat(Array.isArray(r.items) ? r.items : []);
          totalPages1 = r.totalPages || 1;
          p++;
        } while (p <= totalPages1);

        const bySoftware = new Map();
        for (const v of all) {
          const key = `${v.softwareName} ${v.softwareVersion}`;
          if (!bySoftware.has(key)) bySoftware.set(key, []);
          bySoftware.get(key).push(v);
        }
        const softwareGroups = Array.from(bySoftware.entries())
          .map(([key, vulns]) => {
            const top = [...vulns].sort((a, b) => (b.cvssScore ?? 0) - (a.cvssScore ?? 0))[0];
            const bon = vulns.find((v) => v.comitBonNumero);
            return {
              key,
              softwareName: vulns[0].softwareName,
              softwareVersion: vulns[0].softwareVersion,
              vulns,
              maxSeverity: top?.cvssSeverity,
              maxCvssScore: top?.cvssScore ?? null,
              cveCount: vulns.length,
              vulnerabilityIds: vulns.filter((v) => !v.isResolved).map((v) => v.id),
              comitBonNumero: bon?.comitBonNumero || 0,
              comitBonLinkedAt: bon?.comitBonLinkedAt,
              comitBonLinkedBy: bon?.comitBonLinkedBy,
              comitClientId: vulns[0]?.comitClientId || 0,
            };
          })
          .sort((a, b) => {
            const r = (SEV_RANK[b.maxSeverity] || 0) - (SEV_RANK[a.maxSeverity] || 0);
            return r !== 0 ? r : b.cveCount - a.cveCount;
          });

        setDeviceDetails((d) => ({ ...d, [targetAgentId]: { loading: false, error: false, softwareGroups } }));
      } catch {
        setDeviceDetails((d) => ({ ...d, [targetAgentId]: { loading: false, error: true, softwareGroups: [] } }));
      }
    },
    [severity, resolved, search]
  );

  const toggleExpandDevice = (targetAgentId) => {
    setExpandedDevices((exp) => {
      const next = new Set(exp);
      if (next.has(targetAgentId)) next.delete(targetAgentId);
      else next.add(targetAgentId);
      return next;
    });
    if (!deviceDetails[targetAgentId]) loadDeviceDetail(targetAgentId);
  };

  const toggleExpandSoftware = (ck) =>
    setExpandedSoftware((exp) => {
      const next = new Set(exp);
      if (next.has(ck)) next.delete(ck);
      else next.add(ck);
      return next;
    });

  const toggleSelectDeviceSoftware = (ck) =>
    setSelectedDeviceSoftware((sel) => (sel.includes(ck) ? sel.filter((x) => x !== ck) : [...sel, ck]));

  // Résoudre une CVE depuis l'arborescence : rafraîchit les compteurs niveau 1
  // (serveur) ET le détail mis en cache de CET appareil (sinon la ligne
  // resterait affichée « active » dans le cache jusqu'au prochain changement
  // de filtre).
  const resolveCveInDevice = async (targetAgentId, id) => {
    if (!confirm('Marquer cette CVE comme résolue ?')) return;
    await api.post('/platform/vulnerabilities/' + id + '/resolve');
    await Promise.all([load(), loadDeviceDetail(targetAgentId)]);
  };

  const collapseAllDevices = () => {
    setExpandedDevices(new Set());
    setExpandedSoftware(new Set());
  };

  // Reflète vue, filtres et pagination dans l'URL. `replace` plutôt qu'une
  // écriture directe du hash : poser trois filtres ne doit pas empiler trois
  // entrées dans l'historique (cf. AlertsPage).
  useEffect(() => {
    const q = {
      view: view !== 'device' ? view : null,
      severity: severity || null,
      resolved: resolved === '' ? 'all' : resolved === 'true' ? 'true' : null, // 'false' = défaut, pas sérialisé
      clientId,
      agentId,
      search: search || null,
      page: view === 'cve' && page > 1 ? page : null,
      pageSize: view === 'cve' && pageSize !== 50 ? pageSize : null,
    };
    const hash = viewToHash('vulnerabilities', null, null, q);
    if (window.location.hash !== hash) window.history.replaceState(null, '', hash);
  }, [view, severity, resolved, clientId, agentId, search, page, pageSize]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const params = new URLSearchParams();
      if (agentId) params.set('agentId', agentId);
      if (clientId) params.set('clientId', clientId);
      try {
        const statuses = await api.get('/platform/vulnerabilities/scan-status?' + params);
        if (active) {
          setScanStatuses(Array.isArray(statuses) ? statuses : []);
          setScanStatusError(!Array.isArray(statuses));
        }
      } catch {
        if (active) setScanStatusError(true);
      }
    };
    refresh();
    const interval = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [agentId, clientId]);

  const incompleteScans = scanStatuses.filter((s) => s.state !== 'complete');

  const sortedVulns = sortRows(vulns, (v, key) => {
    if (key === 'detectedAt') return v.detectedAt ? new Date(v.detectedAt) : null;
    return v[key];
  });

  const setSeverityFilter = (v) => { setSeverity(v); setPage(1); };
  const setResolvedFilter = (v) => { setResolved(v); setPage(1); };
  const setClientFilter = (id) => { setClientId(id || null); setPage(1); };
  const setViewFilter = (v) => { setView(v); setPage(1); };

  const toggleSelect = (id) =>
    setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const toggleSelectGroup = (key) =>
    setSelectedGroups((sel) => (sel.includes(key) ? sel.filter((x) => x !== key) : [...sel, key]));
  const toggleExpand = (key) =>
    setExpanded((exp) => {
      const next = new Set(exp);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const detachBon = async (id) => {
    await assignBon(0, { vulnerabilityIds: [id] });
    load();
  };

  const resolveOne = async (id) => {
    if (!confirm('Marquer cette CVE comme résolue ?')) return;
    await api.post('/platform/vulnerabilities/' + id + '/resolve');
    load();
  };

  // Union des lignes assignables (non résolues) des groupes sélectionnés — c'est
  // ce que le serveur assigne réellement au bon (cf. CLAUDE.md, extension au
  // groupe des vulnérabilités, lignes non résolues seulement).
  const selectedGroupVulnIds = groups
    .filter((g) => selectedGroups.includes(groupKey(g)))
    .flatMap((g) => g.vulnerabilityIds || []);

  // Union des lignes assignables des groupes (logiciel, appareil) sélectionnés
  // dans l'arborescence « Par poste » — même principe que `selectedGroupVulnIds`
  // ci-dessus, mais scopé par appareil (clé `${agentId}::${softwareKey}`).
  const selectedDeviceVulnIds = selectedDeviceSoftware.flatMap((ck) => {
    const agId = ck.slice(0, ck.indexOf('::'));
    const swKey = ck.slice(ck.indexOf('::') + 2);
    const group = deviceDetails[agId]?.softwareGroups?.find((g) => g.key === swKey);
    return group?.vulnerabilityIds || [];
  });

  const assignCible =
    view === 'cve'
      ? { vulnerabilityIds: selected }
      : view === 'software'
        ? { vulnerabilityIds: selectedGroupVulnIds }
        : { vulnerabilityIds: selectedDeviceVulnIds };
  const assignCount =
    view === 'cve' ? selected.length : view === 'software' ? selectedGroups.length : selectedDeviceSoftware.length;

  const filterChip = (active) =>
    `px-2.5 py-1 rounded-md border text-[11px] cursor-pointer transition-colors ${
      active
        ? 'border-blue-500 bg-blue-500/10 text-blue-400 font-semibold'
        : 'border-rule bg-transparent text-muted hover:bg-card-hi hover:text-fg-2'
    }`;

  const viewToggleBtn = (active) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors ${
      active
        ? 'border-blue-500 bg-blue-500/10 text-blue-400 font-semibold'
        : 'border-rule bg-transparent text-muted hover:bg-card-hi hover:text-fg-2'
    }`;

  const rangeText = (() => {
    if (view !== 'cve') return '';
    if (total === 0) return '0 sur 0';
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return `${from.toLocaleString('fr-FR')}–${to.toLocaleString('fr-FR')} sur ${total.toLocaleString('fr-FR')}`;
  })();

  return (
    <div>
      <div className="flex justify-between items-center mb-5 gap-3">
        <h1 className="text-[22px] font-bold text-fg m-0 flex items-center gap-2">
          <ShieldAlert size={20} strokeWidth={1.75} className="text-amber-400" /> Vulnérabilités (CVE)
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setViewFilter('device')} className={viewToggleBtn(view === 'device')}>
            <Laptop size={13} strokeWidth={1.75} /> Par poste
          </button>
          <button onClick={() => setViewFilter('software')} className={viewToggleBtn(view === 'software')}>
            <LayoutGrid size={13} strokeWidth={1.75} /> Par logiciel
          </button>
          <button onClick={() => setViewFilter('cve')} className={viewToggleBtn(view === 'cve')}>
            <List size={13} strokeWidth={1.75} /> Par CVE
          </button>
          {assignCount > 0 && canAssignBon && (
            <button
              onClick={() => setAssignOpen(true)}
              title="Rattacher les CVE sélectionnées à un bon de travail ComIT déjà créé"
              className="rounded-lg py-1.5 px-4 text-blue-400 cursor-pointer text-xs font-semibold bg-blue-500/12 border border-blue-500/30"
            >
              {'Assigner à un bon (' + assignCount + ')'}
            </button>
          )}
        </div>
      </div>

      {(scanStatusError || incompleteScans.length > 0) && (
        <div role="status" className="bg-warn/10 border border-warn/30 rounded-xl px-4 py-3 mb-4 text-xs text-fg">
          <p className="m-0 font-semibold">
            {scanStatusError ? 'État des scans Linux indisponible.' : `Scan CVE Linux non confirmé pour ${incompleteScans.length} appareil(s).`}
          </p>
          <p className="mt-1 mb-0 text-muted">Les résultats précédents sont conservés. Une liste vide ne confirme pas l’absence de vulnérabilités.</p>
          {incompleteScans.map((s) => (
            <p key={s.agentId} className="mt-2 mb-0"><strong>{s.computerName}</strong> — {s.message}</p>
          ))}
        </div>
      )}

      <div className="bg-card border border-rule rounded-2xl px-5 py-4 mb-4">
        <div className="flex items-center gap-2 mb-3 text-fg-2 text-xs font-bold uppercase tracking-wider">
          <Filter size={13} strokeWidth={1.75} /> Filtres
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-muted-2">Sévérité :</span>
          {[
            ['', 'Toutes'],
            ['CRITICAL', 'Critical'],
            ['HIGH', 'High'],
            ['MEDIUM', 'Medium'],
            ['LOW', 'Low'],
          ].map(([v, l]) => (
            <button key={v || 'all'} onClick={() => setSeverityFilter(v)} className={filterChip(severity === v)}>
              {l}
            </button>
          ))}
          <span className="text-[11px] text-muted-2 ml-3">Statut :</span>
          {[
            ['false', 'Non résolues'],
            ['true', 'Résolues'],
            ['', 'Toutes'],
          ].map(([v, l]) => (
            <button key={v || 'all'} onClick={() => setResolvedFilter(v)} className={filterChip(resolved === v)}>
              {l}
            </button>
          ))}
          {canFilterByClient && (
            <>
              <span className="text-[11px] text-muted-2 ml-3">Client :</span>
              <ClientPicker
                value={clientId}
                onChange={setClientFilter}
                placeholder="Tous les clients"
                className="w-[200px]"
              />
            </>
          )}
          {agentId && (
            <span className="ml-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-blue-500 bg-blue-500/10 text-blue-400 text-[11px] font-semibold">
              Appareil : {initialAgentName || vulns[0]?.computerName || '#' + agentId}
              <button
                onClick={() => { setAgentId(null); setPage(1); }}
                title="Retirer le filtre appareil et voir tout le parc"
                className="bg-transparent border-none p-0 text-blue-400 cursor-pointer inline-flex hover:text-blue-300"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          )}
          <input
            placeholder="Recherche (logiciel, CVE, appareil, client)…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="bg-sidebar border border-rule rounded-lg px-3 py-1.5 text-fg-2 text-xs outline-none ml-auto min-w-[220px] focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-2.5 flex-wrap">
        <div className="text-[11px] text-muted-2">
          {loading
            ? 'Chargement…'
            : view === 'cve'
              ? `${total.toLocaleString('fr-FR')} vulnérabilité${total > 1 ? 's' : ''}`
              : view === 'software'
                ? `${groups.length.toLocaleString('fr-FR')} logiciel${groups.length > 1 ? 's' : ''} touché${groups.length > 1 ? 's' : ''}, ${groups.reduce((a, g) => a + g.cveCount, 0).toLocaleString('fr-FR')} CVE`
                : `${deviceTotal.toLocaleString('fr-FR')} appareil${deviceTotal > 1 ? 's' : ''}, ${deviceRows.reduce((a, r) => a + r.total, 0).toLocaleString('fr-FR')} CVE`}
        </div>
        {view === 'device' && expandedDevices.size > 0 && (
          <button
            onClick={collapseAllDevices}
            className="flex items-center gap-1 text-[11px] text-muted hover:text-fg-2 bg-transparent border border-rule-2 rounded-md px-2 py-1 cursor-pointer"
          >
            <ChevronsUp size={12} strokeWidth={2} /> Tout replier
          </button>
        )}
      </div>
      {view === 'device' && severity && (
        <div className="text-[10.5px] text-muted-2 mb-2.5 -mt-1.5">
          Comptes limités à la sévérité filtrée ({severity.toLowerCase()}).
        </div>
      )}

      {view === 'device' ? (
        <VulnerabilityDeviceTree
          rows={deviceSort.sortRows(deviceRows, (r, key) => {
            if (key === 'lastDetectedAt') return r.lastDetectedAt ? new Date(r.lastDetectedAt) : null;
            return r[key];
          })}
          loading={loading}
          headerProps={deviceSort.headerProps}
          arrow={deviceSort.arrow}
          expanded={expandedDevices}
          onToggleExpand={toggleExpandDevice}
          details={deviceDetails}
          expandedSoftware={expandedSoftware}
          onToggleSoftware={toggleExpandSoftware}
          canAssignBon={canAssignBon}
          selectedSoftware={selectedDeviceSoftware}
          onToggleSelectSoftware={toggleSelectDeviceSoftware}
          canResolve={can('ManageVulnerabilities')}
          onResolveCve={resolveCveInDevice}
          navigate={navigate}
        />
      ) : view === 'software' ? (
        <div className="bg-card border border-rule rounded-2xl overflow-hidden">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-sidebar">
              <tr>
                <th className="px-3 py-2.5 w-[24px] border-b border-rule" />
                {canAssignBon && (
                  <th className="px-3 py-2.5 w-[30px] border-b border-rule">
                    <input
                      type="checkbox"
                      checked={selectedGroups.length === groups.length && groups.length > 0}
                      onChange={() =>
                        setSelectedGroups((sel) => (sel.length === groups.length ? [] : groups.map(groupKey)))
                      }
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">Logiciel</th>
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">Version</th>
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">Sévérité max</th>
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">CVE</th>
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">Appareils</th>
                <th className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule">Bon ComIT</th>
                <th className="px-3 py-2.5 border-b border-rule" />
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={canAssignBon ? 8 : 7} className="px-3 py-10 text-center text-muted-2 text-xs">
                    {loading ? 'Chargement…' : 'Aucune vulnérabilité ne correspond aux filtres'}
                  </td>
                </tr>
              ) : (
                groups.map((g) => {
                  const key = groupKey(g);
                  const isOpen = expanded.has(key);
                  const devices = g.devices || [];
                  const uniformComitClientId =
                    devices.length > 0 && devices.every((d) => d.comitClientId === devices[0].comitClientId)
                      ? devices[0].comitClientId || 0
                      : 0;
                  const appareilLabel =
                    devices.length > 1
                      ? `${devices[0]?.computerName} (+${devices.length - 1})`
                      : devices[0]?.computerName || '';
                  return (
                    <Fragment key={key}>
                      <tr className="border-b border-sidebar">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => toggleExpand(key)}
                            className="bg-transparent border-none p-0 text-muted cursor-pointer inline-flex hover:text-fg-2"
                            title={isOpen ? 'Replier' : 'Détailler'}
                          >
                            {isOpen ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                          </button>
                        </td>
                        {canAssignBon && (
                          <td className="px-3 py-2">
                            {(g.vulnerabilityIds || []).length > 0 && (
                              <input
                                type="checkbox"
                                checked={selectedGroups.includes(key)}
                                onChange={() => toggleSelectGroup(key)}
                              />
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 text-fg-2 truncate max-w-[220px]" title={g.softwareName}>
                          {g.softwareName}
                        </td>
                        <td className="px-3 py-2 text-muted font-mono text-[11px]">{g.softwareVersion}</td>
                        <td className="px-3 py-2">
                          <SevBadge sev={g.maxSeverity} />
                        </td>
                        <td className="px-3 py-2 text-fg-2 font-mono">{g.cveCount}</td>
                        <td className="px-3 py-2 text-fg-2 font-mono">{devices.length}</td>
                        <td className="px-3 py-2">
                          {g.comitBonNumero ? (
                            <ComitBonChip
                              compact
                              numero={g.comitBonNumero}
                              linkedAt={g.comitBonLinkedAt}
                              linkedBy={g.comitBonLinkedBy}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!g.comitBonNumero && (g.vulnerabilityIds || []).length > 0 && (
                            <ComitTaskButton
                              compact
                              texte={buildGroupTaskText(g)}
                              comitClientId={uniformComitClientId}
                              appareil={appareilLabel}
                              cible={{ vulnerabilityIds: g.vulnerabilityIds }}
                            />
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-sidebar bg-sidebar/40">
                          <td />
                          {canAssignBon && <td />}
                          <td colSpan={canAssignBon ? 6 : 5} className="px-3 py-3">
                            <div className="mb-2">
                              <div className="text-[10px] text-muted-2 uppercase font-semibold mb-1.5">
                                Appareils ({devices.length})
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {devices.map((d) => (
                                  <button
                                    key={d.agentId}
                                    onClick={() => navigate('deviceDetail', { device: d.agentId })}
                                    title={d.clientName || ''}
                                    className="px-2 py-0.5 rounded-md border border-rule-2 bg-card text-blue-400 text-[11px] font-mono cursor-pointer hover:bg-card-hi"
                                  >
                                    {d.computerName}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] text-muted-2 uppercase font-semibold mb-1.5">
                                CVE ({(g.cveIds || []).length})
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {(g.cveIds || []).map((id) => (
                                  <a
                                    key={id}
                                    href={`https://nvd.nist.gov/vuln/detail/${id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-0.5 rounded-md border border-rule-2 bg-card text-blue-400 text-[11px] font-mono no-underline hover:underline"
                                  >
                                    {id}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="bg-card border border-rule rounded-2xl overflow-hidden">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-sidebar">
                <tr>
                  {canAssignBon && (
                    <th className="px-3 py-2.5 w-[30px] border-b border-rule">
                      <input
                        type="checkbox"
                        checked={selected.length === sortedVulns.length && sortedVulns.length > 0}
                        onChange={() =>
                          setSelected((sel) => (sel.length === sortedVulns.length ? [] : sortedVulns.map((v) => v.id)))
                        }
                      />
                    </th>
                  )}
                  {[
                    { key: 'cvssSeverity', label: 'Sévérité' },
                    { key: 'cvssScore', label: 'CVSS' },
                    { key: 'cveId', label: 'CVE' },
                    { key: 'softwareName', label: 'Logiciel' },
                    { key: 'softwareVersion', label: 'Version' },
                    { key: 'computerName', label: 'Appareil' },
                    { key: 'clientName', label: 'Client' },
                    { key: 'detectedAt', label: 'Détectée' },
                    { key: 'isResolved', label: 'Statut' },
                    { key: null, label: '' },
                  ].map((c) => (
                    <th
                      key={c.label || 'spacer'}
                      {...headerProps(c.key)}
                      className="px-3 py-2.5 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap"
                    >
                      {c.label}
                      {arrow(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedVulns.length === 0 ? (
                  <tr>
                    <td colSpan={canAssignBon ? 11 : 10} className="px-3 py-10 text-center text-muted-2 text-xs">
                      {loading ? 'Chargement…' : 'Aucune vulnérabilité ne correspond aux filtres'}
                    </td>
                  </tr>
                ) : (
                  sortedVulns.map((v) => (
                    <tr key={v.id} className={`border-b border-sidebar ${v.isResolved ? 'opacity-60' : ''}`}>
                      {canAssignBon && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.includes(v.id)}
                            onChange={() => toggleSelect(v.id)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <SevBadge sev={v.cvssSeverity} />
                      </td>
                      <td className="px-3 py-2 text-fg-2 font-mono">
                        {v.cvssScore != null ? v.cvssScore.toFixed(1) : '—'}
                      </td>
                      <td className="px-3 py-2 text-blue-400 font-mono">
                        <a
                          href={`https://nvd.nist.gov/vuln/detail/${v.cveId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="no-underline hover:underline"
                        >
                          {v.cveId}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-fg-2 truncate max-w-[200px]" title={v.softwareName}>
                        {v.softwareName}
                      </td>
                      <td className="px-3 py-2 text-muted font-mono text-[11px]">{v.softwareVersion}</td>
                      <td
                        className="px-3 py-2 text-blue-400 cursor-pointer font-mono"
                        onClick={() => navigate('deviceDetail', { device: v.agentId })}
                      >
                        {v.computerName}
                      </td>
                      <td
                        className="px-3 py-2 text-muted text-[11.5px] truncate max-w-[160px]"
                        title={v.clientName || ''}
                      >
                        {v.clientName || '—'}
                      </td>
                      <td className="px-3 py-2 text-muted text-[11px]">{fmtDate(v.detectedAt)}</td>
                      <td className="px-3 py-2">
                        {v.isResolved ? (
                          <div>
                            <span className="text-ok text-[11px] inline-flex items-center gap-1">
                              <Check size={11} strokeWidth={2.5} /> Résolue
                            </span>
                            {fmtResolvedBy(v.resolvedBy) && (
                              <div
                                className="text-[10px] text-muted-2 mt-0.5 truncate max-w-[150px]"
                                title={`${fmtResolvedBy(v.resolvedBy)}${v.resolvedAt ? ' — ' + fmtDate(v.resolvedAt) : ''}`}
                              >
                                {fmtResolvedBy(v.resolvedBy)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-crit text-[11px]">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1.5 items-center justify-end">
                          {!v.isResolved && (
                            <button
                              onClick={() => resolveOne(v.id)}
                              className="bg-transparent border border-rule-3 rounded-md px-2 py-0.5 text-muted text-[10px] cursor-pointer hover:text-fg-2"
                            >
                              Résoudre
                            </button>
                          )}
                          {/* Déjà rattachée à un bon : on montre le numéro au lieu du
                              bouton de création — la mise à jour est prise en charge. */}
                          {v.comitBonNumero ? (
                            <ComitBonChip
                              compact
                              numero={v.comitBonNumero}
                              linkedAt={v.comitBonLinkedAt}
                              linkedBy={v.comitBonLinkedBy}
                              onDetach={canAssignBon ? () => detachBon(v.id) : null}
                            />
                          ) : (
                            <ComitTaskButton
                              compact
                              texte={buildCveTaskText(v)}
                              comitClientId={v.comitClientId || 0}
                              appareil={v.computerName || ''}
                              cible={{ vulnerabilityIds: [v.id] }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 text-[11px] text-muted-2">
                <span>{rangeText}</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="bg-card border border-rule-2 rounded-md px-1.5 py-1 text-fg-2 text-[11px] outline-none"
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>{s} / page</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`px-3 py-1 rounded-md border text-[11px] bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2 ${page <= 1 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages || p, p + 1))}
                  disabled={page >= totalPages}
                  className={`px-3 py-1 rounded-md border text-[11px] bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2 ${page >= totalPages ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {assignOpen && (
        <ComitBonAssignModal
          cible={assignCible}
          resume={
            view === 'cve'
              ? selected.length + ' CVE sélectionnée(s), étendues au logiciel de chaque appareil'
              : selectedGroups.length + ' logiciel(s) sélectionné(s), ' + selectedGroupVulnIds.length + ' CVE non résolue(s)'
          }
          onClose={() => setAssignOpen(false)}
          onDone={() => {
            setSelected([]);
            setSelectedGroups([]);
            load();
          }}
        />
      )}
    </div>
  );
}
